import { styled, Box, CircularProgress } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, themeAlpine } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { THEME_MODES } from '../theme';

const Container = styled(Box, { shouldForwardProp: (prop) => !prop.startsWith('$') })(
  ({ $empty, $loading }) => ({
    position: 'relative',
    display: 'flex',
    width: '100%',
    flex: 1,
    minHeight: 0,
    height: '100%',
    '& > div': {
      flex: 1,
    },
    '& .ag-body-horizontal-scroll': {
      marginTop: '4px',
    },
    '& .ag-center-cols-viewport': {
      minHeight: $empty ? 150 : 'unset', // minimum height is necessary to avoid an empty grid looking weird
    },
    '& .ag-row': {
      '& .ag-cell': {
        display: 'flex',
        fontSize: 14,
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      },
    },
    '& .ag-row .ag-cell.ag-column-first': {
      justifyContent: 'flex-start',
      textAlign: 'left',
      paddingLeft: 24,
    },
    '& .ag-row .ag-cell.ag-column-last': {
      paddingRight: 24,
    },
    '& .ag-row .ag-cell.ag-cell-left': {
      justifyContent: 'flex-start',
      textAlign: 'left',
    },
    '& .ag-row .ag-cell.ag-cell-right': {
      justifyContent: 'flex-end',
      textAlign: 'right',
    },
    '& .ag-header .ag-header-cell.ag-column-first': {
      paddingLeft: 24,
    },
    '& .ag-header .ag-header-cell.ag-column-last': {
      paddingRight: 24,
    },
    ...($loading && {
      '& .ag-overlay-no-rows-wrapper': {
        display: 'none',
      },
    }),
  })
);

const LoadingOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
});

const ColumnHeader = (props) => {
  const firstDisplayedColumn = props.api?.getAllDisplayedColumns?.()?.[0];
  const isFirstDisplayedColumn =
    firstDisplayedColumn?.getColId?.() && props.column?.getColId?.()
      ? firstDisplayedColumn.getColId() === props.column.getColId()
      : false;
  const headerAlign = props.column?.colDef?.headerAlign;

  const justifyContent =
    headerAlign === 'right'
      ? 'flex-end'
      : headerAlign === 'left' || isFirstDisplayedColumn
        ? 'flex-start'
        : 'center';
  const textAlign =
    headerAlign === 'right'
      ? 'right'
      : headerAlign === 'left' || isFirstDisplayedColumn
        ? 'left'
        : 'center';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent,
        width: '100%',
        textAlign,
      }}
    >
      {props.displayName}
    </Box>
  );
};

const getRowHeight = (params) => {
  return params.data?.id === 'footer' ? 76 : 60;
};

const LOCALE_TEXT = {
  noRowsToShow: 'Aucune ligne à afficher',
};

const Grid = ({
  rowData,
  columnDefs,
  defaultColDef,
  sx,
  className,
  localeText,
  loading = false,
  ...agGridProps
}) => {
  const muiTheme = useTheme();

  const agGridTheme = useMemo(
    () =>
      themeAlpine.withParams({
        accentColor: 'transparent',
        backgroundColor:
          muiTheme.palette.mode === THEME_MODES.DARK
            ? '#303030'
            : muiTheme.palette.background.paper,
        headerBackgroundColor:
          muiTheme.palette.mode === THEME_MODES.DARK
            ? '#232323'
            : muiTheme.palette.background.default,
        textColor: muiTheme.palette.text.primary,
        oddRowBackgroundColor:
          muiTheme.palette.mode === THEME_MODES.DARK
            ? '#232323'
            : muiTheme.palette.background.paper,
        headerTextColor:
          muiTheme.palette.mode === THEME_MODES.DARK
            ? muiTheme.palette.text.secondary
            : muiTheme.palette.text.primary,
        rowHoverColor:
          muiTheme.palette.mode === THEME_MODES.DARK ? 'inherit' : 'rgba(158, 221, 83, 0.12)',
        fontFamily: 'inherit',
        fontSize: 14,
        headerFontSize: 14,
        cellEditingShadow: false,
        cellEditingBorder: false,
        cellHorizontalPadding: 15,
        cardShadow: false,
        focusShadow: false,
        headerColumnResizeHandleWidth: 0,
        headerFontWeight: 500,
        columnBorder: false,
        headerColumnBorder: false,
        pinnedColumnBorder: false,
        borderRadius: 0,
        wrapperBorder: false,
        wrapperBorderRadius: 0,
      }),
    [muiTheme]
  );

  const mergedDefaultColDef = useMemo(
    () => ({
      ...defaultColDef,
      resizable: false,
      sortable: false,
      headerComponent: defaultColDef?.headerComponent || ColumnHeader,
    }),
    [defaultColDef]
  );

  return (
    <Container sx={sx} className={className} $empty={!rowData?.length} $loading={loading}>
      <AgGridReact
        suppressScrollOnNewData
        theme={agGridTheme}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={mergedDefaultColDef}
        modules={[AllCommunityModule]}
        getRowHeight={getRowHeight}
        headerHeight={60}
        localeText={{ ...LOCALE_TEXT, ...localeText }}
        {...agGridProps}
      />
      {loading && (
        <LoadingOverlay>
          <CircularProgress />
        </LoadingOverlay>
      )}
    </Container>
  );
};

export default Grid;
